import { useEffect, useState, useCallback } from 'react';
import { Mail, MessageSquare, Phone, Search, RefreshCw, X, Loader2, ScrollText, ChevronRight } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useSort, SortTh, TableHeadRow } from '@/components/ui/sortable-table';
import { formatDateTime } from '@/lib/dateFormat';
import api from '@/lib/api';
import { SelectField } from '@/components/ui/SelectField';

type CommChannel = 'email' | 'whatsapp' | 'sms';

interface CommLog {
  id: string;
  channel: CommChannel;
  to: string;
  summary: string;
  status: string;
  error: string | null;
  at: string;
}

interface CommLogDetail {
  id: string;
  channel: CommChannel;
  to: string;
  status: string;
  error: string | null;
  at: string;
  subject?: string;
  body?: string | null;
  messageType?: string;
  text?: string | null;
  templateName?: string | null;
  params?: string[];
  mediaUrl?: string | null;
}

// [pill classes, dot color] per status
const STATUS_STYLE: Record<string, [string, string]> = {
  sent: ['bg-emerald-50 text-emerald-700', 'bg-emerald-500'],
  delivered: ['bg-emerald-50 text-emerald-700', 'bg-emerald-500'],
  read: ['bg-emerald-50 text-emerald-700', 'bg-emerald-500'],
  failed: ['bg-red-50 text-red-700', 'bg-red-500'],
  pending: ['bg-amber-50 text-amber-700', 'bg-amber-500'],
  queued: ['bg-amber-50 text-amber-700', 'bg-amber-500'],
};

const StatusBadge = ({ status }: { status: string }) => {
  const [cls, dot] = STATUS_STYLE[status] ?? ['bg-slate-100 text-slate-600', 'bg-slate-400'];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium capitalize ${cls}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />{status}
    </span>
  );
};

const CHANNEL_CHIP: Record<CommChannel, { cls: string; icon: JSX.Element }> = {
  email:    { cls: 'bg-indigo-50 text-indigo-700',   icon: <Mail className="h-3.5 w-3.5" /> },
  whatsapp: { cls: 'bg-emerald-50 text-emerald-700', icon: <MessageSquare className="h-3.5 w-3.5" /> },
  sms:      { cls: 'bg-orange-50 text-orange-700',   icon: <Phone className="h-3.5 w-3.5" /> },
};

const ChannelChip = ({ channel }: { channel: CommChannel }) => {
  const c = CHANNEL_CHIP[channel] ?? CHANNEL_CHIP.email;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium ${c.cls}`}>
      {c.icon}
      <span className="uppercase">{channel}</span>
    </span>
  );
};

export function CommunicationLogsPage() {
  const [logs, setLogs] = useState<CommLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [channel, setChannel] = useState('');
  const [failedOnly, setFailedOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const [selected, setSelected] = useState<CommLog | null>(null);
  const [detail, setDetail] = useState<CommLogDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, limit };
      if (channel) params.channel = channel;
      if (failedOnly) params.status = 'failed';
      if (search.trim()) params.search = search.trim();
      const { data } = await api.get('/communications/logs', { params });
      setLogs(data.logs || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, channel, failedOnly, search]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { setPage(1); }, [channel, failedOnly, search, limit]);

  const openDetail = async (l: CommLog) => {
    setSelected(l);
    setDetail(null);
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/communications/logs/${l.channel}/${l.id}`);
      setDetail(data);
    } catch {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };
  const closeDetail = () => { setSelected(null); setDetail(null); };

  // Client-side sort of the current page (datatable behaviour).
  const { sorted, sort, onSort } = useSort(logs);

  const fmt = (d: string) => formatDateTime(d);

  return (
    <DashboardLayout title="Communication Logs">
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
            <ScrollText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Communication Logs</h1>
            <p className="text-sm text-slate-500">Every outbound email, WhatsApp &amp; SMS — click a row to see exactly what was sent.</p>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2.5 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search recipient or subject…"
              className="h-9 w-full rounded-lg border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-indigo-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-100"
            />
          </div>
          <SelectField
            value={channel}
            onChange={e => setChannel(e.target.value)}
            className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-100"
          >
            <option value="">All channels</option>
            <option value="email">Email</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="sms">SMS</option>
          </SelectField>
          <button
            onClick={() => setFailedOnly(v => !v)}
            className={`inline-flex h-9 items-center gap-1.5 rounded-lg border px-3 text-sm font-medium transition-colors ${
              failedOnly
                ? 'border-red-200 bg-red-50 text-red-600'
                : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${failedOnly ? 'bg-red-500' : 'bg-slate-300'}`} />
            Failed only
          </button>
          <button
            onClick={fetchLogs}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-600 hover:bg-slate-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <TableHeadRow>
                <SortTh label="Sr." sortable={false} className="w-14" />
                <SortTh label="Channel" sortKey="channel" sort={sort} onSort={onSort} className="w-32" />
                <SortTh label="Recipient" sortKey="to" sort={sort} onSort={onSort} />
                <SortTh label="Summary" sortKey="summary" sort={sort} onSort={onSort} />
                <SortTh label="Status" sortKey="status" sort={sort} onSort={onSort} className="w-32" />
                <SortTh label="Time" sortKey="at" sort={sort} onSort={onSort} className="w-40" />
                <SortTh label="" sortable={false} className="w-8 px-2" />
              </TableHeadRow>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && logs.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">No communication logs found</td></tr>
              ) : sorted.map((l, idx) => (
                <tr key={`${l.channel}-${l.id}`} onClick={() => openDetail(l)} className="group cursor-pointer transition-colors hover:bg-indigo-50/40">
                  <td className="px-4 py-3 font-medium text-slate-400">{(page - 1) * limit + idx + 1}</td>
                  <td className="px-4 py-3"><ChannelChip channel={l.channel} /></td>
                  <td className="px-4 py-3 font-medium text-slate-800">{l.to}</td>
                  <td className="px-4 py-3 max-w-md">
                    <div className="truncate text-slate-700">{l.summary}</div>
                    {l.error && <div className="truncate text-xs text-red-500" title={l.error}>{l.error}</div>}
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={l.status} /></td>
                  <td className="px-4 py-3 text-xs text-slate-500">{fmt(l.at)}</td>
                  <td className="px-2 py-3 text-slate-300 group-hover:text-indigo-400"><ChevronRight className="h-4 w-4" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer / pagination */}
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-slate-500">
          <div className="flex items-center gap-2">
            <span>Rows per page</span>
            <SelectField
              value={limit}
              onChange={e => setLimit(Number(e.target.value))}
              className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-700 focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-100"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </SelectField>
            <span className="text-slate-400">· {total} total</span>
          </div>
          <div className="flex items-center gap-2">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 hover:bg-slate-50 disabled:opacity-40">Prev</button>
            <span className="px-1">Page {page} / {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 hover:bg-slate-50 disabled:opacity-40">Next</button>
          </div>
        </div>
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={closeDetail}>
          <div className="flex h-full w-full max-w-xl flex-col bg-white shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <ChannelChip channel={selected.channel} />
                  <StatusBadge status={selected.status} />
                </div>
                <p className="mt-2 truncate text-sm font-medium text-slate-800">To: {selected.to}</p>
                <p className="text-xs text-slate-400">{fmt(selected.at)}</p>
              </div>
              <button onClick={closeDetail} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {detailLoading ? (
                <div className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
              ) : !detail ? (
                <p className="text-sm text-slate-400">Could not load details.</p>
              ) : (
                <div className="space-y-4">
                  {detail.error && (
                    <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-xs text-red-700">
                      <span className="font-semibold">Error:</span> {detail.error}
                    </div>
                  )}

                  {detail.channel === 'email' ? (
                    <>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Subject</p>
                        <p className="mt-1 text-sm font-medium text-slate-800">{detail.subject || '—'}</p>
                      </div>
                      <div>
                        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">Message</p>
                        {detail.body ? (
                          <iframe title="email-body" sandbox="" srcDoc={detail.body} className="h-[60vh] w-full rounded-lg border border-slate-200 bg-white" />
                        ) : (
                          <p className="text-sm text-slate-400">Body not recorded for this email (older message).</p>
                        )}
                      </div>
                    </>
                  ) : detail.channel === 'sms' ? (
                    <div>
                      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">Message</p>
                      <div className="whitespace-pre-wrap rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800">
                        {detail.text || '—'}
                      </div>
                    </div>
                  ) : (
                    <>
                      {detail.templateName && (
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Template</p>
                          <p className="mt-1 inline-block rounded-md bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-700">{detail.templateName}</p>
                        </div>
                      )}
                      {Array.isArray(detail.params) && detail.params.length > 0 && (
                        <div>
                          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">Parameters</p>
                          <div className="space-y-1">
                            {detail.params.map((p, i) => (
                              <div key={i} className="flex gap-2 text-sm">
                                <span className="w-8 flex-shrink-0 font-mono text-slate-400">{`{{${i + 1}}}`}</span>
                                <span className="text-slate-800">{p}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <div>
                        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">Message</p>
                        <div className="whitespace-pre-wrap rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800">
                          {detail.text || '(template message — see parameters above)'}
                        </div>
                      </div>
                      {detail.mediaUrl && (
                        <a href={detail.mediaUrl} target="_blank" rel="noreferrer" className="inline-block text-sm text-indigo-600 hover:underline">View attached media</a>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
